import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "./SmoothScroll";

const EASE = [0.16, 1, 0.3, 1];

function Word({ children, delay }) {
  return (
    <span className="mask-line">
      <motion.span
        style={{ display: "block", willChange: "transform" }}
        initial={{ y: "108%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onConsult }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const canvasY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const canvasScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex flex-col justify-end overflow-hidden pb-20 sm:pb-32"
      data-testid="hero"
    >
      {/* Soft editorial depth layer (parallax) */}
      <motion.div
        style={{ y: canvasY, scale: canvasScale }}
        className="absolute inset-0 z-0 pointer-events-none"
        aria-hidden
      >
        <div className="absolute right-[6%] top-[14%] w-[46vw] h-[46vw] max-w-[620px] max-h-[620px] rounded-full"
             style={{ background: "radial-gradient(circle at 40% 35%, rgba(210,162,140,0.45), rgba(210,162,140,0) 68%)", filter: "blur(6px)" }} />
        <div className="absolute left-[-8%] bottom-[8%] w-[38vw] h-[38vw] max-w-[520px] max-h-[520px] rounded-full"
             style={{ background: "radial-gradient(circle at 50% 50%, rgba(168,176,153,0.4), rgba(168,176,153,0) 70%)", filter: "blur(8px)" }} />
      </motion.div>

      {/* Floating hero product (exact site image, background removed) with a soft ground shadow */}
      <motion.div
        style={{ y: canvasY }}
        className="absolute inset-0 z-[5] pointer-events-none hidden md:block"
        aria-hidden
      >
        <div className="absolute right-[4%] top-[13%] w-[118px] lg:w-[144px]">
          <motion.img
            src="/uploads/ghk_cutout.png"
            alt="GHK-Cu 100mg peptide vial"
            className="relative z-10 w-full h-auto"
            data-testid="hero-product-ghk"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, -18, 0] }}
            transition={{
              opacity: { duration: 1, delay: 0.9 },
              y: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.9 },
            }}
          />
          <motion.div
            className="absolute left-1/2 -translate-x-1/2 -bottom-3 w-[62%] h-[16px] rounded-[50%] bg-black"
            style={{ filter: "blur(11px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.3, 0.15, 0.3], scaleX: [1, 0.8, 1] }}
            transition={{
              opacity: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.9 },
              scaleX: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.9 },
            }}
          />
        </div>
      </motion.div>

      {/* top meta row */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute top-24 sm:top-28 left-0 w-full z-10 px-5 sm:px-8"
      >
        <div className="max-w-[1500px] mx-auto flex justify-between items-start">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="overline text-[10px] max-w-[9.5rem] leading-relaxed text-[var(--ink-soft)]"
          >
            Clinical-grade skin, wellness &amp; research compounds — priced below market.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 1 }}
            className="overline text-right text-[var(--ink-soft)] hidden sm:block"
          >
            No subscription
            <br />
            No gatekeeping
          </motion.p>
        </div>
      </motion.div>

      {/* Big title */}
      <motion.div
        style={{ y: titleY }}
        className="relative z-10 px-5 sm:px-8 w-full"
      >
        <div className="max-w-[1500px] mx-auto">
          <h1 className="font-display leading-[0.82] tracking-tighter text-[19vw] sm:text-[17vw] lg:text-[15vw]">
            <Word delay={0.35}>Skin, solved</Word>
            <span className="block">
              <Word delay={0.5}>
                by <span className="italic text-[var(--terra)]">science</span>.
              </Word>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 1, ease: EASE }}
            className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-12"
          >
            <p className="font-body text-base sm:text-lg max-w-md text-[var(--ink-soft)] leading-relaxed">
              Tretinoin, hydroquinone and research-grade actives that dermatologists
              actually prescribe — without the clinic markup or the monthly plan.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onConsult}
                className="btn-ink px-7 py-4 rounded-full inline-flex items-center gap-2"
                data-testid="hero-consult-btn"
              >
                Check eligibility
                <ArrowDownRight size={15} strokeWidth={1.6} />
              </button>
              <button
                onClick={() => scrollToId("skin")}
                className="btn-outline px-7 py-4 rounded-full"
                data-testid="hero-shop-btn"
              >
                Shop now
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
